/*
 * Cart drawer extras: free gift with purchase, in-cart subscription
 * upgrade/downgrade, and in-cart variant switching.
 *
 * Works with the theme's cart-drawer web component: every cart mutation
 * requests the 'cart-drawer' section and emits 'update:cart:drawer' so the
 * drawer re-renders itself. The Liquid side (cart-free-gift.liquid etc.)
 * decides WHAT should happen; these components only execute the cart calls.
 */
(() => {
  'use strict';

  const SECTION_ID = 'cart-drawer';
  const FGWP_DECLINED_KEY = 'tts-fgwp-declined';

  /* Audit trail: every explicit gift decision is stamped onto the cart as
     an attribute, which Shopify copies onto the order (admin > order >
     Additional details). Lets support confirm "did this customer decline
     the gift?" instead of guessing. keepalive so the write survives an
     immediate navigation to checkout. Customers never see attributes. */
  const recordGiftDecision = (value) => {
    return fetch(routes.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { 'Free gift': value } }),
      keepalive: true,
    }).catch(() => {});
  };
  const routes = window.routes || {};
  const CART_ADD_URL = routes.cart_add_url || '/cart/add.js';
  const CART_CHANGE_URL = routes.cart_change_url || '/cart/change.js';

  /* Same audit-trail idea for the in-cart subscription upsell: when the
     customer clicks Upgrade, stamp which product and frequency onto the cart
     attributes (admin > order > Additional details), so support can see how
     a subscription was started. Appends across products; repeat clicks on
     the same product don't duplicate. Best-effort - never blocks the cart. */
  const recordSubUpsell = async (productTitle, planName) => {
    try {
      const key = 'Subscription upsell (cart)';
      const entry = planName ? `${productTitle}: ${planName}` : productTitle;
      const response = await fetch(`${routes.cart_url || '/cart'}.js`);
      const cart = await response.json();
      const prior = (cart.attributes && cart.attributes[key]) || '';
      if (prior && prior.includes(entry)) return;
      const value = prior ? `${prior} | ${entry}` : entry;
      await fetch(routes.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [key]: value } }),
        keepalive: true,
      });
    } catch (error) {
      /* audit stamp is best-effort; never block the upgrade */
    }
  };

  /* Serialize cart mutations so rapid clicks can't race each other */
  let cartBusy = false;

  async function cartPost(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data || data.errors) {
      const message = data && (data.description || data.errors || data.message);
      throw new Error(typeof message === 'string' ? message : 'Cart request failed');
    }
    return data;
  }

  function rerenderDrawer(response) {
    if (response?.sections?.[SECTION_ID]) {
      window.eventBus.emit('update:cart:drawer', response);
    } else {
      window.eventBus.emit('render:cart:drawer');
    }
    updateCartCount();
  }

  async function updateCartCount() {
    try {
      const response = await fetch(`${routes.cart_url || '/cart'}.js`);
      const cart = await response.json();
      window.wetheme?.updateCartCount?.(cart);
    } catch (error) {
      /* non-critical */
    }
  }

  async function runCartAction(element, action) {
    if (cartBusy) return;
    cartBusy = true;
    element?.classList.add('is-loading');
    try {
      await action();
    } catch (error) {
      console.error('[cart-extras]', error);
      window.eventBus.emit('render:cart:drawer'); // resync UI with real cart state
    } finally {
      cartBusy = false;
      element?.classList.remove('is-loading');
    }
  }

  /* ============================================================
   * Free gift with purchase
   * data-action="add"    -> auto-add gift (hidden directive)
   * data-action="remove" -> auto-remove gift (hidden directive)
   * data-action="offer"  -> chooser card with variant select
   * data-action="line"   -> wraps the gift line's static qty + remove button
   * ============================================================ */
  let fgwpAutoAddFailed = false;

  class CartFreeGift extends HTMLElement {
    connectedCallback() {
      window.wetheme?.webcomponentRegistry?.register({ key: 'component-cart-free-gift' });
      switch (this.dataset.action) {
        case 'add':
          this.autoAdd();
          break;
        case 'remove':
          this.autoRemove();
          break;
        case 'offer':
          this.querySelector('[data-fgwp-add]')?.addEventListener('click', () => this.addFromOffer());
          this.querySelector('[data-fgwp-variant]')?.addEventListener('change', (e) => {
            /* show the chosen variant's photo */
            const src = e.currentTarget.selectedOptions[0]?.dataset.image;
            const img = this.querySelector('[data-fgwp-image] img');
            if (img && src) {
              img.removeAttribute('srcset');
              img.src = src;
            }
          });
          /* "No thanks" collapses to a one-line banner; "Claim" re-expands */
          this.querySelector('[data-fgwp-dismiss]')?.addEventListener('click', () => this.setCollapsed(true, true));
          this.querySelector('[data-fgwp-reopen]')?.addEventListener('click', () => this.setCollapsed(false, true));
          if (sessionStorage.getItem(FGWP_DECLINED_KEY)) this.setCollapsed(true, false);
          break;
        case 'line':
          this.querySelector('[data-fgwp-remove]')?.addEventListener('click', () => this.declineAndRemove());
          break;
      }
    }

    setCollapsed(collapsed, persist) {
      this.classList.toggle('cart-free-gift--collapsed', collapsed);
      const mini = this.querySelector('[data-fgwp-mini]');
      if (mini) mini.hidden = !collapsed;
      if (persist) {
        if (collapsed) {
          sessionStorage.setItem(FGWP_DECLINED_KEY, '1');
          recordGiftDecision('Declined by customer (No thanks in cart)');
        } else {
          sessionStorage.removeItem(FGWP_DECLINED_KEY);
        }
      }
    }

    addGift(variantId) {
      return cartPost(CART_ADD_URL, {
        items: [{ id: variantId, quantity: 1, properties: { _fgwp: '1' } }],
        sections: SECTION_ID,
      });
    }

    autoAdd() {
      if (fgwpAutoAddFailed) return; // e.g. gift went out of stock; don't retry-loop
      if (sessionStorage.getItem(FGWP_DECLINED_KEY)) return;
      runCartAction(this, async () => {
        try {
          rerenderDrawer(await this.addGift(Number(this.dataset.variantId)));
          recordGiftDecision('Added automatically');
        } catch (error) {
          fgwpAutoAddFailed = true;
          throw error;
        }
      });
    }

    removeBody() {
      /* key-based targeting is immune to stale line numbers (e.g. second tab) */
      return this.dataset.lineKey
        ? { id: this.dataset.lineKey, quantity: 0, sections: SECTION_ID }
        : { line: Number(this.dataset.line), quantity: 0, sections: SECTION_ID };
    }

    autoRemove() {
      runCartAction(this, async () => {
        rerenderDrawer(await cartPost(CART_CHANGE_URL, this.removeBody()));
        fgwpAutoAddFailed = false;
        recordGiftDecision('Removed (order dropped below gift minimum)');
      });
    }

    addFromOffer() {
      const picked = this.querySelector('[data-fgwp-options] input:checked')
        || this.querySelector('[data-fgwp-variant]');
      const addButton = this.querySelector('[data-fgwp-add]');
      const variantId = Number(picked ? picked.value : addButton?.dataset.variantId);
      if (!variantId) return;
      sessionStorage.removeItem(FGWP_DECLINED_KEY);
      runCartAction(this, async () => {
        rerenderDrawer(await this.addGift(variantId));
        recordGiftDecision('Claimed in cart');
      });
    }

    declineAndRemove() {
      /* Customer said no: remember it so we don't auto-add again this session */
      sessionStorage.setItem(FGWP_DECLINED_KEY, '1');
      runCartAction(this, async () => {
        rerenderDrawer(await cartPost(CART_CHANGE_URL, this.removeBody()));
        recordGiftDecision('Removed after claiming');
      });
    }
  }

  /* ============================================================
   * Subscription upgrade / frequency change / downgrade
   * ============================================================ */
  class CartLineSubscription extends HTMLElement {
    connectedCallback() {
      window.wetheme?.webcomponentRegistry?.register({ key: 'component-cart-line-subscription' });
      this.querySelector('[data-sub-upgrade]')?.addEventListener('click', (e) => {
        /* One-time lines show ONLY the button (no frequency UI before opting
           in - it reads as an active schedule). Upgrade applies the default
           plan; the customer then adjusts frequency in the dropdown that
           appears, which lists plans across ALL groups. */
        const planId = Number(e.currentTarget.dataset.planId);
        const planName = e.currentTarget.dataset.planName || '';
        this.changeSellingPlan(planId, { recordUpsell: true, planName });
      });
      this.querySelector('[data-sub-plan-select]')?.addEventListener('change', (e) => {
        this.changeSellingPlan(Number(e.currentTarget.value));
      });
      this.querySelector('[data-sub-remove]')?.addEventListener('click', () => {
        this.changeSellingPlan('');
      });
    }

    changeSellingPlan(sellingPlan, options) {
      runCartAction(this, async () => {
        rerenderDrawer(await cartPost(CART_CHANGE_URL, {
          line: Number(this.dataset.line),
          quantity: Number(this.dataset.quantity),
          selling_plan: sellingPlan,
          sections: SECTION_ID,
        }));
        if (options?.recordUpsell) {
          await recordSubUpsell(this.dataset.productTitle || 'Unknown item', options.planName);
        }
      });
    }
  }

  /* ============================================================
   * Variant switching on a cart line (remove + re-add, keeping
   * quantity, properties and selling plan)
   * ============================================================ */
  class CartVariantSelect extends HTMLElement {
    connectedCallback() {
      window.wetheme?.webcomponentRegistry?.register({ key: 'component-cart-variant-select' });
      this.querySelector('[data-variant-select]')?.addEventListener('change', (e) => {
        this.swapVariant(Number(e.currentTarget.value));
      });
    }

    swapVariant(newVariantId) {
      if (!newVariantId || newVariantId === Number(this.dataset.currentVariant)) return;
      runCartAction(this, async () => {
        /* NEVER send properties: {} - Shopify treats a {}-props line as distinct
           from a no-props line internally while DISPLAYING them merged, which
           desyncs every line-number- and key-based operation afterwards. */
        let properties = null;
        try {
          const parsed = JSON.parse(this.dataset.properties || 'null');
          if (parsed && Object.keys(parsed).length > 0) properties = parsed;
        } catch (error) {
          properties = null;
        }
        const item = {
          id: newVariantId,
          quantity: Number(this.dataset.quantity),
        };
        if (properties) item.properties = properties;
        if (this.dataset.sellingPlan) item.selling_plan = Number(this.dataset.sellingPlan);
        /* Add the new variant FIRST so a failure can never lose the item;
           then remove the old line by its stable key (indices shift on add).
           If the markup predates data-line-key (mid-deploy version skew),
           resolve the key from the live cart before touching anything. */
        let lineKey = this.dataset.lineKey;
        if (!lineKey) {
          const cartResponse = await fetch(`${routes.cart_url || '/cart'}.js`);
          const cart = await cartResponse.json();
          const oldVariantId = Number(this.dataset.currentVariant);
          lineKey = cart.items.find((lineItem) => lineItem.variant_id === oldVariantId)?.key;
        }
        if (!lineKey) {
          window.eventBus.emit('render:cart:drawer');
          return;
        }
        await cartPost(CART_ADD_URL, { items: [item] });
        rerenderDrawer(await cartPost(CART_CHANGE_URL, {
          id: lineKey,
          quantity: 0,
          sections: SECTION_ID,
        }));
      });
    }
  }

  /* ============================================================
   * Compact recommendation rows - one-tap add
   * ============================================================ */
  class CartRecItem extends HTMLElement {
    connectedCallback() {
      window.wetheme?.webcomponentRegistry?.register({ key: 'component-cart-rec-item' });
      this.querySelector('[data-rec-add]')?.addEventListener('click', () => this.addToCart());
      this.querySelector('[data-rec-variant]')?.addEventListener('change', (e) => {
        /* keep the visible price in sync with the selected variant */
        const price = e.currentTarget.selectedOptions[0]?.dataset.price;
        const priceElement = this.querySelector('[data-rec-price]');
        if (price && priceElement) priceElement.textContent = price;
      });
    }

    addToCart() {
      const select = this.querySelector('[data-rec-variant]');
      const addButton = this.querySelector('[data-rec-add]');
      const variantId = Number(select ? select.value : addButton?.dataset.variantId);
      if (!variantId) return;
      runCartAction(this, async () => {
        rerenderDrawer(await cartPost(CART_ADD_URL, {
          items: [{ id: variantId, quantity: 1 }],
          sections: SECTION_ID,
        }));
      });
    }
  }

  /* ============================================================
   * Quantity stepper - targets the line by its stable key, so a
   * stale drawer (second tab, delayed render) can never change
   * the wrong item. Rapid clicks coalesce via a pending target.
   * ============================================================ */
  class CartLineQty extends HTMLElement {
    connectedCallback() {
      window.wetheme?.webcomponentRegistry?.register({ key: 'component-cart-line-qty' });
      this.input = this.querySelector('[data-qty-input]');
      this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.step(1));
      this.querySelector('[data-qty-remove]')?.addEventListener('click', () => {
        this.classList.add('is-removing');
        this.requestQuantity(0);
      });
      this.input?.addEventListener('change', () => {
        const parsed = parseInt(this.input.value, 10);
        this.requestQuantity(Number.isNaN(parsed) || parsed < 0 ? this.renderedQuantity : parsed);
      });
    }

    get renderedQuantity() {
      return parseInt(this.dataset.itemQuantity, 10) || 0;
    }

    step(delta) {
      const shown = parseInt(this.input?.value, 10);
      const base = Number.isNaN(shown) ? this.renderedQuantity : shown;
      this.requestQuantity(Math.max(0, base + delta));
    }

    requestQuantity(quantity) {
      if (this.input) this.input.value = quantity;
      this.pendingQuantity = quantity;
      this.flush();
    }

    async flush() {
      if (this.flushing) return;
      this.flushing = true;
      this.classList.add('is-loading');
      try {
        while (this.pendingQuantity !== undefined) {
          const quantity = this.pendingQuantity;
          this.pendingQuantity = undefined;
          if (quantity === this.renderedQuantity) continue;
          const response = await cartPost(CART_CHANGE_URL, {
            id: this.dataset.lineKey,
            quantity,
            sections: SECTION_ID,
          });
          this.dataset.itemQuantity = String(quantity);
          rerenderDrawer(response);
        }
      } catch (error) {
        console.error('[cart-extras]', error);
        window.eventBus.emit('render:cart:drawer');
      } finally {
        this.flushing = false;
        this.classList.remove('is-loading', 'is-removing');
      }
    }
  }

  /* ============================================================
   * Refresh the drawer's HTML every time it opens, so a tab whose
   * markup went stale (cart changed elsewhere) re-syncs before the
   * customer can interact with outdated lines or prices.
   * ============================================================ */
  const registerOpenRefresh = () => {
    if (!window.eventBus?.on) {
      setTimeout(registerOpenRefresh, 300);
      return;
    }
    window.eventBus.on('open:cart:drawer', () => {
      window.eventBus.emit('render:cart:drawer');
    });
  };
  registerOpenRefresh();

  /* ============================================================
   * Gift note toggle. OFF = the note is removed from the order (so
   * it can't print if they check out), but the words are stashed in
   * sessionStorage; flipping back ON restores and re-saves them.
   * (toggle events don't bubble; use a capturing listener)
   * ============================================================ */
  const GIFT_NOTE_STASH_KEY = 'tts-gift-note-stash';
  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!details.matches?.('details.cart-gift-note')) return;
    const textarea = details.querySelector('textarea');
    if (!textarea) return;
    const updateUrl = routes.cart_update_url || '/cart/update.js';

    if (details.open) {
      /* reopened: bring back the stashed note and save it to the order */
      const stashed = sessionStorage.getItem(GIFT_NOTE_STASH_KEY);
      if (stashed && textarea.value === '') {
        textarea.value = stashed;
        cartPost(updateUrl, { attributes: { gift_note: stashed } });
      }
    } else if (textarea.value !== '') {
      /* closed: keep the words for later, but take them off the order */
      sessionStorage.setItem(GIFT_NOTE_STASH_KEY, textarea.value);
      textarea.value = '';
      cartPost(updateUrl, { attributes: { gift_note: '' } });
    }
  }, true);

  /* ============================================================
   * Discount code - applies via /cart/update.js { discount }
   * ============================================================ */
  class CartDiscountCode extends HTMLElement {
    connectedCallback() {
      window.wetheme?.webcomponentRegistry?.register({ key: 'component-cart-discount-code' });
      this.input = this.querySelector('[data-discount-input]');
      this.error = this.querySelector('[data-discount-error]');
      this.querySelector('[data-discount-apply]')?.addEventListener('click', () => this.apply(this.input?.value));
      this.input?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.apply(this.input.value);
        }
      });
      this.querySelector('[data-discount-remove]')?.addEventListener('click', () => this.apply(''));
      this.querySelector('[data-fgwp-chip-remove]')?.addEventListener('click', (e) => {
        this.removeFreeGift(e.currentTarget.dataset.lineKey);
      });
    }

    removeFreeGift(lineKey) {
      if (!lineKey) return;
      /* same semantics as declining the gift on its line */
      sessionStorage.setItem(FGWP_DECLINED_KEY, '1');
      runCartAction(this, async () => {
        rerenderDrawer(await cartPost(CART_CHANGE_URL, {
          id: lineKey,
          quantity: 0,
          sections: SECTION_ID,
        }));
        recordGiftDecision('Removed after claiming');
      });
    }

    apply(rawCode) {
      const code = (rawCode || '').trim();
      const isRemoval = rawCode === '';
      if (!code && !isRemoval) return;
      runCartAction(this, async () => {
        const updateUrl = routes.cart_update_url || '/cart/update.js';
        const cart = await cartPost(updateUrl, { discount: code, sections: SECTION_ID });
        const applied = (cart.discount_codes || []).some(
          (entry) => entry.code.toLowerCase() === code.toLowerCase() && entry.applicable
        );
        if (!isRemoval && !applied) {
          /* not valid for this cart: clear it again and surface the message.
             Shopify gift cards (incl. LoyaltyLion reward codes) are 16
             characters and can only be redeemed at checkout - point those
             customers to the next page instead of calling the code invalid. */
          await cartPost(updateUrl, { discount: '' });
          if (this.error) {
            const looksLikeGiftCard = /^[a-z0-9]{16}$/i.test(code.replace(/[\s-]/g, ''));
            this.error.classList.toggle('cart-discount__error--giftcard', looksLikeGiftCard);
            this.error.textContent = looksLikeGiftCard
              ? (this.dataset.giftcardText || 'That code is a gift card or loyalty reward. You can’t enter that here, but you can enter it at checkout on the next page!')
              : (this.dataset.errorText || 'That code isn’t valid for your cart.');
            this.error.removeAttribute('hidden');
          }
          return;
        }
        rerenderDrawer(cart);
      });
    }
  }

  /* ============================================================
   * Checkout guard: while the free gift is unlocked but unclaimed,
   * checkout pauses and presents an explicit choice - add the gift,
   * or "continue without it" (which really checks out). A reflexive
   * double-click can't slip past it, and it never dead-ends anyone.
   * ============================================================ */
  let fgwpCheckoutBypassed = false;
  document.addEventListener('click', (event) => {
    /* explicit "no thanks" inside the guard message -> proceed to checkout */
    if (event.target.closest('[data-fgwp-checkout-anyway]')) {
      fgwpCheckoutBypassed = true;
      sessionStorage.setItem(FGWP_DECLINED_KEY, '1');
      /* stamp the decision, then proceed - keepalive covers the navigation */
      recordGiftDecision('Declined at checkout (No thanks)').finally(() => {
        document.getElementById('CartDrawer-Checkout')?.click();
      });
      return;
    }

    const checkoutButton = event.target.closest('#CartDrawer-Checkout');
    if (!checkoutButton || fgwpCheckoutBypassed) return;
    /* an explicit "no thanks" anywhere (card dismiss, line x, chip x) means
       we stop interrupting checkout too */
    if (sessionStorage.getItem(FGWP_DECLINED_KEY)) return;

    const offer = document.querySelector('cart-drawer cart-free-gift[data-action="offer"]');
    if (!offer) return;

    event.preventDefault();
    offer.querySelector('[data-fgwp-guard]')?.removeAttribute('hidden');
    offer.classList.remove('cart-free-gift--attention');
    void offer.offsetWidth; /* restart the pulse animation on repeat clicks */
    offer.classList.add('cart-free-gift--attention');
    offer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    offer.querySelector('[data-fgwp-add]')?.focus({ preventScroll: true });
  });

  if (!customElements.get('cart-free-gift')) customElements.define('cart-free-gift', CartFreeGift);
  if (!customElements.get('cart-line-subscription')) customElements.define('cart-line-subscription', CartLineSubscription);
  if (!customElements.get('cart-variant-select')) customElements.define('cart-variant-select', CartVariantSelect);
  if (!customElements.get('cart-rec-item')) customElements.define('cart-rec-item', CartRecItem);
  if (!customElements.get('cart-discount-code')) customElements.define('cart-discount-code', CartDiscountCode);
  if (!customElements.get('cart-line-qty')) customElements.define('cart-line-qty', CartLineQty);
})();
